/**
 * 이상한 방 - 그리드 오버레이 & 배치 고스트
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3.1절(셀 점유 규칙) · 3.2절(표면 스냅) · 4.1절(그리드 해상도)
 *
 * 이 두 가지는 장식이 아니라 **입력 UX의 본체**다.
 *
 * 그리드 오버레이 - 4.1절이 셀 크기를 "최소 소품의 바운딩 크기"로 못 박은 이유는
 * 1칸 이동이 반드시 눈에 보이게 하기 위해서다. 그런데 격자가 화면에 없으면
 * 배치하는 사람은 자기가 몇 칸을 움직였는지 알 수 없고, 결국 "대충 이 근처"로 놓게 된다.
 *
 * 배치 고스트 - 3.2절의 표면 스냅은 포인터가 가리킨 표면 위 z로 자동으로 붙는 동작인데,
 * **어디에 붙었는지 보여 주지 않으면 스냅은 마술이 아니라 버그로 보인다.**
 * 꾸미기 페이즈가 1분이라 시행착오할 여유가 없으므로, 놓기 전에 결과가 보여야 한다.
 *
 * 배치 가능 여부(녹/적)는 여기서 판정하지 않는다 - 호출부가 verbs.ts `canPlace()`의 결과를
 * `isValid`로 넘긴다. 판정 로직이 렌더 레이어에 복제되는 순간 화면과 서버가 갈린다.
 *
 * ## 격자를 상시 표시하지 않는 이유 (이 파일의 가장 큰 결정)
 *
 * 20m × 20m 바닥 전체에 0.25m 격자를 상시로 깔면 화면이 **게임이 아니라 에디터**가 된다.
 * 발밑에 청사진이 깔려 있으면 실내가 실내로 보이지 않는다는 것이 1인칭 시절의 근거였고,
 * 아이소메트릭에서도 청사진이 깔려 있으면 변화 찾기를 방해한다.
 * 그렇다고 격자를 없애면 위 문단의 이유로 배치가 무너진다.
 *
 * 그래서 **격자는 기본적으로 꺼져 있고, 배치 커서 주변 반경에만 페이드로 나타난다.**
 * - 관찰(수색) 모드: `setGridFocus(overlay, null)` → 완전히 숨김
 * - 에디터 모드: 커서가 가리키는 셀을 중심으로 반경 안쪽만 보인다
 *
 * 페이드는 지오메트리를 다시 굽지 않고 **셰이더 한 줄**로 처리한다. 포인터가 움직일 때마다
 * 선을 재생성하면 그 자체가 프레임 예산이고, 애초에 필요한 것은 알파 하나뿐이다.
 *
 * 오버레이는 커서 높이로 통째로 올라간다(`overlay.position.y`). 책상 위에 컵을 놓을 때
 * 2.5m 아래 바닥에 격자가 있으면 아무 도움이 안 되기 때문이다 - 격자는 "지금 놓이는 평면"이다.
 *
 * 렌더 레이어이므로 Three.js 객체의 제자리 변경을 허용한다(코딩 규칙의 명시적 예외).
 */

import {
  BoxGeometry,
  BufferGeometry,
  Color,
  EdgesGeometry,
  Float32BufferAttribute,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  ShaderMaterial,
  Vector2,
  type Object3D,
} from 'three'

import { CELL_SIZE_METERS, type CatalogEntry, type Cell3, type RoomBounds, type Rot } from '../core/types'
import {
  computeMarkerPlacements,
  computeObjectCenterWorld,
  footprintToMeters,
  rotToRadians,
  setNodeColor,
} from './objectView'

/* --- 그리드 오버레이 ----------------------------------------------------------- */

const GRID_OVERLAY_NAME = 'grid-overlay'

/** 바닥면(y=0)과의 z-파이팅을 피하기 위한 최소 띄움. 눈에 띄지 않을 만큼만 올린다. */
const GRID_Y_OFFSET_METERS = 0.002

/**
 * 굵은 기준선 간격(셀). 0.25m × 4 = 1m.
 * 0.25m 격자만 깔면 선이 너무 촘촘해 거리 감각이 사라진다 - 1m 기준선이 있어야
 * "소파를 세 칸 옮겼다"가 아니라 "소파가 벽에서 떨어졌다"로 읽힌다(4.1절이 노리는 식별성).
 */
const MAJOR_LINE_INTERVAL_CELLS = 4

/**
 * 선 색. 어두운 회색이던 것을 따뜻한 흰색으로 올렸다 -
 * 커서 주변에만 잠깐 나타나는 선이라 어두우면 파스텔 바닥 위에서 아예 안 보인다.
 * 이 값은 **sRGB 그대로** 화면에 쓰인다(아래 셰이더 주석 참고).
 */
const MINOR_LINE_COLOR = 0xf3ece2
const MAJOR_LINE_COLOR = 0xffffff
const MINOR_LINE_OPACITY = 0.22
const MAJOR_LINE_OPACITY = 0.5

/**
 * 완전 불투명 반경과 소멸 반경(미터).
 *
 * 안쪽 0.9m = 셀 3.6칸. 소파(2m)를 놓을 때 물건 폭보다 격자가 좁으면 정렬에 쓸 수 없고,
 * 바깥 2.4m를 넘기면 화면 하단이 통째로 격자로 덮여 "에디터 화면"으로 되돌아간다.
 */
const FOCUS_INNER_RADIUS_METERS = 0.9
const FOCUS_OUTER_RADIUS_METERS = 2.4

/** 선분 하나를 이루는 정점 수(시작점·끝점). */
const VERTICES_PER_SEGMENT = 2
const POSITION_COMPONENTS = 3

/** 셰이더 유니폼 이름. 문자열이 두 곳(생성·갱신)에 나뉘어 있으면 오타가 조용한 무동작이 된다. */
const UNIFORM_FOCUS = 'uFocus'

/**
 * 커서 반경 페이드 셰이더.
 *
 * 색을 변환 없이 그대로 쓰는 것이 의도다. `ShaderMaterial`은 Three의 색공간·톤매핑 청크를
 * 자동으로 붙이지 않으므로 여기서 쓴 값이 **최종 sRGB 픽셀**이 된다.
 * 격자는 조명을 받는 물체가 아니라 UI 오버레이이므로 이 편이 예측 가능하다
 * (조명이 바뀔 때마다 격자 밝기가 따라 변하면 그게 더 이상하다).
 *
 * `discard`는 성능이 아니라 정확성 때문이다 - 알파 0인 선도 깊이는 쓰지 않지만
 * 반투명 정렬 대상으로 남아 다른 반투명 면(유리·고스트)과 순서를 다툰다.
 */
const FOCUS_VERTEX_SHADER = /* glsl */ `
  varying vec2 vPlane;
  void main() {
    vPlane = position.xz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`

const FOCUS_FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 uColor;
  uniform vec2 uFocus;
  uniform float uInner;
  uniform float uOuter;
  uniform float uOpacity;
  varying vec2 vPlane;

  void main() {
    float fade = 1.0 - smoothstep(uInner, uOuter, distance(vPlane, uFocus));
    float alpha = uOpacity * fade;
    if (alpha <= 0.004) discard;
    gl_FragColor = vec4(uColor, alpha);
  }
`

/**
 * 방 크기는 설정·템플릿에서 들어오는 외부 데이터다. 0이나 소수가 섞이면
 * 격자가 조용히 비거나 셀 경계와 어긋나므로 경계에서 막는다.
 */
function assertPositiveBounds(bounds: RoomBounds): void {
  const isValid =
    Number.isInteger(bounds.sizeX) &&
    bounds.sizeX > 0 &&
    Number.isInteger(bounds.sizeZ) &&
    bounds.sizeZ > 0

  if (isValid) return

  throw new RangeError(
    `그리드 오버레이: 방 크기는 양의 정수여야 한다 (sizeX=${bounds.sizeX}, sizeZ=${bounds.sizeZ})`,
  )
}

/** 굵게 그릴 선인가. 방 가장자리는 간격과 무관하게 항상 굵게 - 경계가 곧 배치 가능 범위다(3.1절). */
function isMajorLine(index: number, lastIndex: number): boolean {
  return index === 0 || index === lastIndex || index % MAJOR_LINE_INTERVAL_CELLS === 0
}

function createLineSegments(positions: readonly number[], color: number, opacity: number): LineSegments {
  const geometry = new BufferGeometry()
  geometry.setAttribute('position', new Float32BufferAttribute([...positions], POSITION_COMPONENTS))

  const tint = new Color(color)
  const material = new ShaderMaterial({
    vertexShader: FOCUS_VERTEX_SHADER,
    fragmentShader: FOCUS_FRAGMENT_SHADER,
    transparent: true,
    // 격자는 깊이에 기여하지 않는다. 기록하면 뒤따라 그려지는 반투명 고스트가 격자에 잘린다.
    depthWrite: false,
    uniforms: {
      uColor: { value: tint },
      [UNIFORM_FOCUS]: { value: new Vector2(0, 0) },
      uInner: { value: FOCUS_INNER_RADIUS_METERS },
      uOuter: { value: FOCUS_OUTER_RADIUS_METERS },
      uOpacity: { value: opacity },
    },
  })

  return new LineSegments(geometry, material)
}

/**
 * 바닥 그리드 라인을 만든다. 셀 크기(0.25m) 단위이며 방 크기만큼만 그린다.
 * 격자가 방 밖으로 뻗지 않는 것 자체가 "배치 가능한 범위는 여기까지"라는 시각적 계약이다.
 *
 * **만들어진 직후에는 보이지 않는다.** `setGridFocus`로 커서를 알려 줘야 그 주변만 나타난다.
 * 기본값이 "보임"이면 관찰 모드로 돌리는 것을 잊은 경로가 전부 에디터 화면이 되는데,
 * 그 실수는 화면을 보고도 알아채기 어렵다(격자가 원래 있는 것처럼 보이므로).
 */
export function createGridOverlay(bounds: RoomBounds): Object3D {
  assertPositiveBounds(bounds)

  const width = bounds.sizeX * CELL_SIZE_METERS
  const depth = bounds.sizeZ * CELL_SIZE_METERS
  const y = GRID_Y_OFFSET_METERS

  const minor: number[] = []
  const major: number[] = []

  // z축 방향으로 뻗는 선(= x 위치를 가르는 선)
  for (let ix = 0; ix <= bounds.sizeX; ix += 1) {
    const x = ix * CELL_SIZE_METERS
    const target = isMajorLine(ix, bounds.sizeX) ? major : minor
    target.push(x, y, 0, x, y, depth)
  }

  // x축 방향으로 뻗는 선(= z 위치를 가르는 선)
  for (let iz = 0; iz <= bounds.sizeZ; iz += 1) {
    const z = iz * CELL_SIZE_METERS
    const target = isMajorLine(iz, bounds.sizeZ) ? major : minor
    target.push(0, y, z, width, y, z)
  }

  const overlay = new Group()
  overlay.name = GRID_OVERLAY_NAME
  overlay.visible = false
  // 깊이를 쓰지 않는 반투명 선이라 마지막에 그려야 바닥·러그 위에 얹힌다.
  overlay.renderOrder = 1
  overlay.add(createLineSegments(minor, MINOR_LINE_COLOR, MINOR_LINE_OPACITY))
  overlay.add(createLineSegments(major, MAJOR_LINE_COLOR, MAJOR_LINE_OPACITY))

  return overlay
}

/**
 * 격자가 나타날 중심을 정한다. `null`이면 격자를 통째로 숨긴다.
 *
 * 좌표는 **월드 미터**이며, 오버레이는 x·z로 움직이지 않고 y로만 올라간다 -
 * 선 자체는 방 전체에 이미 깔려 있고 셰이더가 반경 밖을 지우기 때문에,
 * 평면상으로 옮기면 오히려 격자가 셀 경계에서 어긋난다(4.1절의 전제가 깨진다).
 */
export function setGridFocus(
  overlay: Object3D,
  focus: Readonly<{ x: number; y: number; z: number }> | null,
): void {
  if (focus === null) {
    overlay.visible = false
    return
  }

  overlay.visible = true
  overlay.position.y = focus.y

  for (const child of overlay.children) {
    if (!(child instanceof LineSegments)) continue

    const material = child.material
    if (!(material instanceof ShaderMaterial)) continue

    const uniform = material.uniforms[UNIFORM_FOCUS]
    if (uniform?.value instanceof Vector2) uniform.value.set(focus.x, focus.z)
  }
}

/** 오버레이가 담고 있는 선분 개수. 테스트가 격자 밀도를 검증할 때 쓴다. */
export function countGridSegments(overlay: Object3D): number {
  let count = 0

  for (const child of overlay.children) {
    if (!(child instanceof LineSegments)) continue
    const position = child.geometry.getAttribute('position')
    count += position.count / VERTICES_PER_SEGMENT
  }

  return count
}

/* --- 배치 고스트 --------------------------------------------------------------- */

const GHOST_NAME = 'placement-ghost'

const GHOST_ROLE_KEY = 'ghostRole'
const GHOST_ROLE_BODY = 'body'
const GHOST_ROLE_EDGES = 'edges'
const GHOST_ROLE_MARKER = 'marker'

/** 표식 최대 개수 = `rotSymmetry`의 최대값. 고스트는 이만큼 미리 만들어 두고 켜고 끈다. */
const MAX_MARKER_COUNT = 4

/** 반투명 정도. 뒤에 있는 가구가 비쳐야 "여기 이미 뭐가 있다"가 읽힌다. */
const GHOST_BODY_OPACITY = 0.4
const GHOST_EDGE_OPACITY = 0.9

/** 배치 가능/불가 색. 색상뿐 아니라 명도도 벌려 색약에서도 갈리게 한다(팔레트와 같은 원칙). */
const GHOST_VALID_COLOR = 0x5fd68a
const GHOST_INVALID_COLOR = 0xd6455f

/** 고스트 표식은 몸통보다 밝게 - 같은 색이면 반투명 속에서 형태가 묻힌다. */
const GHOST_MARKER_TINT_MIX = 0.45
const GHOST_MARKER_TINT_TARGET = 0xffffff

const UNIT_SIZE = 1

function createGhostMaterial(opacity: number): MeshStandardMaterial {
  return new MeshStandardMaterial({
    transparent: true,
    opacity,
    // 반투명 표면끼리 서로를 가려 얼룩지는 것을 막는다. 고스트는 깊이에 기여할 필요가 없다.
    depthWrite: false,
  })
}

/**
 * 배치 미리보기 메시를 만든다. **한 번 만들어 계속 재사용하는 객체**다 -
 * 포인터가 움직일 때마다 새로 만들면 지오메트리 생성/해제가 프레임마다 돌아간다.
 *
 * 그래서 모든 자식이 유닛 박스(1×1×1)이고, `updateGhost()`가 스케일로 크기를 맞춘다.
 * 크기 변화는 `entry`가 바뀔 때만 발생하므로 스케일 한 번이면 충분하다.
 */
export function createGhostMesh(): Object3D {
  const ghost = new Group()
  ghost.name = GHOST_NAME
  // 가리키는 셀이 정해지기 전에는 아무것도 보여 주지 않는다. 호출부가 updateGhost로 켠다.
  ghost.visible = false

  const body = new Mesh(new BoxGeometry(UNIT_SIZE, UNIT_SIZE, UNIT_SIZE), createGhostMaterial(GHOST_BODY_OPACITY))
  body.userData[GHOST_ROLE_KEY] = GHOST_ROLE_BODY
  ghost.add(body)

  ghost.add(createGhostEdges())

  for (let index = 0; index < MAX_MARKER_COUNT; index += 1) {
    const marker = new Mesh(
      new BoxGeometry(UNIT_SIZE, UNIT_SIZE, UNIT_SIZE),
      createGhostMaterial(GHOST_BODY_OPACITY),
    )
    marker.userData[GHOST_ROLE_KEY] = GHOST_ROLE_MARKER
    marker.visible = false
    ghost.add(marker)
  }

  return ghost
}

/**
 * 고스트 외곽선.
 *
 * `depthTest: false`인 이유: 3.1절이 "가려진 위치는 허용한다"고 명시했으므로
 * 소파 뒤·책상 밑에도 배치할 수 있어야 하는데, 그때 고스트가 가구에 완전히 먹히면
 * 어디에 놓이는지 알 수 없다. 외곽선만 뚫고 보이게 해서 위치는 알리되,
 * 몸통은 정상적으로 가려지게 두어 "가려진 자리"라는 사실 자체는 유지한다.
 */
function createGhostEdges(): LineSegments {
  const source = new BoxGeometry(UNIT_SIZE, UNIT_SIZE, UNIT_SIZE)
  const geometry = new EdgesGeometry(source)
  // EdgesGeometry는 정점을 복사해 가므로 원본은 즉시 해제한다(GPU 자원 누수 방지).
  source.dispose()

  const edges = new LineSegments(
    geometry,
    new LineBasicMaterial({ transparent: true, opacity: GHOST_EDGE_OPACITY, depthTest: false }),
  )
  edges.userData[GHOST_ROLE_KEY] = GHOST_ROLE_EDGES

  return edges
}

/**
 * 고스트를 특정 셀·소품·회전에 맞춰 갱신한다.
 *
 * 위치·회전·표식 계산을 objectView.ts와 공유하는 것이 이 함수의 핵심이다.
 * 고스트가 실제 배치 결과와 한 칸이라도 다르게 그려지면 표면 스냅(3.2절)은
 * 도움이 아니라 거짓말이 된다. 그래서 좌표 변환을 여기서 다시 구현하지 않는다.
 *
 * @param isValid verbs.ts `canPlace()`의 결과. 경계 밖이거나 셀이 점유됐으면 false.
 */
export function updateGhost(
  ghost: Object3D,
  cell: Cell3,
  entry: CatalogEntry,
  rot: Rot,
  isValid: boolean,
): void {
  const center = computeObjectCenterWorld(cell, entry, rot)
  ghost.position.set(center.x, center.y, center.z)
  ghost.rotation.y = rotToRadians(rot)
  ghost.visible = true

  const size = footprintToMeters(entry.footprint)
  const bodyTint = new Color(isValid ? GHOST_VALID_COLOR : GHOST_INVALID_COLOR)
  const markerTint = bodyTint.clone().lerp(new Color(GHOST_MARKER_TINT_TARGET), GHOST_MARKER_TINT_MIX)

  for (const child of ghost.children) {
    const role = readGhostRole(child)
    if (role !== GHOST_ROLE_BODY && role !== GHOST_ROLE_EDGES) continue

    child.scale.set(size.x, size.y, size.z)
    setNodeColor(child, bodyTint)
  }

  applyGhostMarkers(ghost, entry, markerTint)
}

/**
 * 방향 표식을 소품의 `rotSymmetry`에 맞춰 켜고 끈다.
 * 실제 오브젝트와 같은 계산(`computeMarkerPlacements`)을 쓰므로
 * "고스트에는 앞면이 있었는데 놓고 보니 없다"가 발생하지 않는다(4.2절 부수 요건).
 */
function applyGhostMarkers(ghost: Object3D, entry: CatalogEntry, tint: Color): void {
  const placements = computeMarkerPlacements(entry.footprint, entry.rotSymmetry)
  const markers = ghost.children.filter((child) => readGhostRole(child) === GHOST_ROLE_MARKER)

  markers.forEach((marker, index) => {
    const placement = placements[index]
    if (placement === undefined) {
      marker.visible = false
      return
    }

    marker.visible = true
    marker.position.set(placement.offset.x, placement.offset.y, placement.offset.z)
    marker.scale.set(placement.size.x, placement.size.y, placement.size.z)
    setNodeColor(marker, tint)
  })
}

function readGhostRole(node: Object3D): string | undefined {
  const value: unknown = node.userData[GHOST_ROLE_KEY]
  return typeof value === 'string' ? value : undefined
}
